
import { ActivityLog } from '@/models/LearnTypes';
import { Lesson } from '@/models/Lesson';
import { MutationHooks } from '../useLevelProgression.types';
import { useRef } from 'react';

interface UseLevelActivityTrackingProps {
  userId: string | undefined;
  selectedLesson: Lesson | null;
  mutations: MutationHooks;
  setPreviouslyCompletedLevels: React.Dispatch<React.SetStateAction<number[]>>;
  setLevelCompletionCounts: React.Dispatch<React.SetStateAction<Record<number, number>>>;
}

export const useLevelActivityTracking = ({
  userId,
  selectedLesson,
  mutations,
  setPreviouslyCompletedLevels,
  setLevelCompletionCounts
}: UseLevelActivityTrackingProps) => {
  // Add a ref to track if we've already checked levels for this lesson
  const checkedLessonRef = useRef<string | null>(null);
  
  // Check activity logs to find previously completed levels for the current lesson
  const checkPreviouslyCompletedLevels = async () => {
    if (!userId || !selectedLesson) return;
    
    // Skip if we've already checked this lesson to prevent infinite loops
    if (checkedLessonRef.current === selectedLesson.id) {
      console.debug(`Already checked completed levels for lesson ${selectedLesson.id}, skipping`);
      return;
    }
    
    console.debug(`Checking completed levels for lesson ${selectedLesson.id}`);
    checkedLessonRef.current = selectedLesson.id;
    
    try {
      const activities = await mutations.getActivities(userId);
      
      // Filter activities for the current lesson and level completions
      const levelActivities = activities.filter(activity => 
        activity.type === 'level-completed' && 
        activity.lessonId === selectedLesson.id &&
        typeof activity.levelIndex === 'number'
      );
      
      // Count completions per level
      const completionCounts: Record<number, number> = {};
      levelActivities.forEach(activity => {
        if (typeof activity.levelIndex === 'number') {
          completionCounts[activity.levelIndex] = (completionCounts[activity.levelIndex] || 0) + 1;
        }
      });
      
      setLevelCompletionCounts(completionCounts);
      
      // Get unique level indices
      const uniqueLevelIndices = [...new Set(levelActivities.map(activity => activity.levelIndex))];
      setPreviouslyCompletedLevels(uniqueLevelIndices);
      
      console.log('Previously completed levels:', uniqueLevelIndices);
      console.log('Level completion counts:', completionCounts);
      
    } catch (error) {
      console.error('Error checking previously completed levels:', error);
    }
  };
  
  // Method to reset the check flag when selecting a new lesson
  const resetLessonCheck = () => {
    checkedLessonRef.current = null;
  };
  
  return {
    checkPreviouslyCompletedLevels,
    resetLessonCheck
  };
};
